import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, AlertIcon, Box, Spinner, useToast } from "@chakra-ui/react";
import Navbar from "./Navbar";
import WatermarkBackground from "./components/WatermarkBackground";
import { useAuth } from "./AuthContext";
import { getTokenOrganisationIds } from "./utils/ownership";
import { getSubmission } from "./services/PlacingAPI";
import { showApiErrorToast } from "./utils/showApiToast";
import BrokerSubmissionView from "./components/placing/BrokerSubmissionView";
import CarrierSubmissionView from "./components/placing/CarrierSubmissionView";

/**
 * A submission, as the broker that owns it or as a carrier it was sent to.
 * Which view is decided by the data: the broker's organisation owns it;
 * anyone else can only see it because it was sent to one of their parties.
 */
const PlacingSubmissionPage = () => {
  const { submissionId } = useParams();
  const { user } = useAuth();
  const toast = useToast();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setSubmission(await getSubmission(submissionId));
      setError(null);
    } catch (err) {
      setError(err);
      showApiErrorToast(toast, err, { title: "Couldn't load submission" });
    } finally {
      setLoading(false);
    }
  }, [submissionId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const isBroker =
    !!submission?.organisationOwnerId &&
    getTokenOrganisationIds(user).includes(submission.organisationOwnerId);

  return (
    <Box position="relative" minHeight="100vh">
      <WatermarkBackground />
      <Navbar />
      <Box p={6} maxW="1400px" mx="auto">
        {loading ? (
          <Spinner />
        ) : error || !submission ? (
          <Alert status="error"><AlertIcon />{error?.message || "Submission not found."}</Alert>
        ) : isBroker ? (
          <BrokerSubmissionView submission={submission} onChanged={load} />
        ) : (
          <CarrierSubmissionView submission={submission} onChanged={load} />
        )}
      </Box>
    </Box>
  );
};

export default PlacingSubmissionPage;
